import { useDispatch, useSelector } from "react-redux";
import useTask from "./useTask";
import { matchApi } from "@/api";
import {
  appendFrameBoxes,
  cutOff,
  updateSuperTrack,
  setChangedKeyFrames,
  mergeSuperTrack,
  setNewBoxes,
} from "@/store/supertrackSlice";
import { CUT_MODES } from "@/constants";
import { useCallback } from "react";
import { toast } from "react-toastify";
import { useLocation, useParams } from "react-router-dom";

export const useActions = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { trackId: currentSuperTrackId } = useParams();
  const { changedKeyFrames, newBoxes } = useSelector(
    (state) => state.superTracks
  );
  const { taskId, frameStart, frameEnd } = useTask();

  const cut = useCallback(
    async (matchId, superTrackId, trackId, cutFrame, mode) => {
      try {
        const isMain =
          location.pathname.startsWith(`/panel/markup/super-tracks/`) &&
          Number(superTrackId) === Number(currentSuperTrackId);

        // Cut tracks
        const { supertrack, tracks } = await matchApi.postTrackCut({
          superTrackId,
          matchId,
          frame: cutFrame,
          trackId: Number(trackId) >= 0 ? trackId : -1,
          pipe_conflict_id: taskId ? taskId : null,
          track_data: isMain && changedKeyFrames.length > 0 ? newBoxes : null,
          changed_frames: isMain ? changedKeyFrames : null,
          task_range: [frameStart, frameEnd],
        });
        dispatch(setNewBoxes({}));
        dispatch(appendFrameBoxes({ newBoxes: tracks }));
        if (isMain) {
          dispatch(setChangedKeyFrames([]));
        }
        if (mode === CUT_MODES.CUT) {
          dispatch(
            updateSuperTrack({
              superTrackId,
              tracks: supertrack,
            })
          );
        } else if (
          [CUT_MODES.CUT_OFF_RIGHT, CUT_MODES.CUT_OFF_LEFT].includes(mode)
        ) {
          const newTrack = supertrack.find(
            (t) => t.frame_start === cutFrame + 1
          );

          // Create a super track
          const newSuperTrack = await matchApi.createSuperTrack(matchId);
          if (!newSuperTrack) return;

          const targetTrackId =
            mode === CUT_MODES.CUT_OFF_RIGHT ? newTrack.track_id : trackId;
          // Move tracks
          replace(matchId, superTrackId, newSuperTrack.super_track_uid, [
            targetTrackId,
          ]);
          dispatch(
            cutOff({
              tracks: supertrack,
              superTrackId,
              trackId: targetTrackId,
              newSuperTrack,
            })
          );
        }
      } catch (err) {
        console.log("err", err);
        toast.error("Something went wrong while cutting");
      }
    },
    [
      taskId,
      newBoxes,
      changedKeyFrames,
      location,
      currentSuperTrackId,
      frameStart,
      frameEnd,
    ]
  );

  const merge = useCallback(
    async (videoId, stId, newId) => {
      dispatch(
        mergeSuperTrack({
          targetId: stId,
          superTrackId: newId,
        })
      );
      matchApi
        .mergeSuperTrack({
          id: Number(newId),
          matchId: videoId,
          superTrackId: Number(stId),
          pipe_conflict_id: taskId ? taskId : null,
          task_range: [frameStart, frameEnd],
        })
        .then((res) => {})
        .catch((err) => {
          toast.error("Something went wrong while merging");
        });
    },
    [taskId, frameStart, frameEnd]
  );

  const replace = useCallback(
    async (matchId, fromId, toId, trackIds) => {
      await matchApi.moveTracks({
        matchId: matchId,
        fromId: Number(fromId),
        toId: Number(toId),
        trackIds,
        pipe_conflict_id: taskId ? taskId : null,
        task_range: [frameStart, frameEnd],
      });
    },
    [taskId, frameStart, frameEnd]
  );

  const substitute = useCallback(async (matchId, superTrackUid) => {
    try {
      await matchApi.addTask({
        match: matchId,
        task_type: "supertrack",
        task: {
          isBall: false,
          super_track_uid: parseInt(superTrackUid),
        },
      });
    } catch (error) {
      console.error(error);
      toast(`Something went wrong. ${error.message}`);
    }
  }, []);

  return {
    cut,
    merge,
    replace,
    substitute,
  };
};
